import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import { useShareBaseUrl, buildShareUrl } from "@/lib/share";
import { AppFrame, TopBar, WhatsAppFAB } from "@/components/Shell";
import Breadcrumbs from "@/components/Breadcrumbs";
import ShareButtons from "@/components/ShareButtons";
import { Phone, Clock } from "lucide-react";

export default function PublicProduct() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(false);
  const shareBase = useShareBaseUrl();

  useEffect(() => {
    (async () => {
      try {
        const s = await api.get("/settings"); setSettings(s.data);
        const p = await api.get(`/public/product/${slug}`);
        setProduct(p.data);
      } catch { setError(true); }
    })();
  }, [slug]);

  if (error) return (
    <AppFrame>
      <TopBar title="Not found" subtitle="Product" onBack={false} />
      <div className="px-5 mt-8 text-center">
        <div className="font-display-italic text-2xl">this piece isn't here</div>
        <p className="text-xs mt-2" style={{ color: "var(--ink-2)" }}>It may have been removed or is unavailable.</p>
        <Link to="/" className="inline-block mt-4 text-[11px] tracking-editorial-sm uppercase underline underline-offset-[6px]" style={{ color: "var(--ink)" }}>Back to store</Link>
      </div>
    </AppFrame>
  );

  if (!product) return (
    <AppFrame>
      <TopBar title="Loading..." subtitle="Product" onBack={false} />
      <div className="px-5 mt-8 animate-pulse space-y-4">
        <div className="aspect-[4/5] rounded-3xl" style={{ background: "var(--bg-alt)" }} />
      </div>
    </AppFrame>
  );

  const images = product.images?.length ? product.images : ["https://images.unsplash.com/photo-1705326454924-f6777522b030?w=800&fit=crop"];
  const hasMrp = product.mrp && product.mrp > product.selling_price;
  const discount = hasMrp ? Math.round(((product.mrp - product.selling_price) / product.mrp) * 100) : 0;
  const isLowStock = product.availability === "in_stock" && typeof product.stock_quantity === "number" && product.stock_quantity > 0 && product.stock_quantity <= 5;
  const waNum = (settings?.whatsapp_number || "").replace(/[^0-9]/g, "");
  const msg = isLowStock
    ? `Hi! I'd like to reserve ${product.name} (only ${product.stock_quantity} left). Can you hold one for me?`
    : `Hello, I am interested in ${product.name}. Is it available?`;
  const waHref = `https://wa.me/${waNum}?text=${encodeURIComponent(msg)}`;
  const telHref = `tel:${settings?.whatsapp_number || ""}`;

  const crumbs = [
    { name: "Home", to: "/" },
    ...(product.breadcrumbs || []).map((c) => ({ name: c.name, to: `/c/${c.slug}` })),
    { name: product.name },
  ];

  const shareUrl = buildShareUrl(shareBase, `/p/${product.slug || slug}`);

  return (
    <AppFrame>
      <TopBar
        title={product.name}
        subtitle={product.category_name || "Product"}
        right={<ShareButtons title={product.name} url={shareUrl} testidPrefix="pd-share" />}
      />
      <div className="px-5 mt-3">
        <Breadcrumbs items={crumbs} />
      </div>

      <div className="px-5 mt-4">
        <div className="aspect-[4/5] rounded-3xl overflow-hidden relative" style={{ background: "#F1E8D8" }}>
          <img src={resolveImage(images[imgIdx])} alt={product.name} className="w-full h-full object-cover" />
          {hasMrp && (
            <div className="absolute top-3 left-3 backdrop-blur text-[10px] tracking-editorial-sm uppercase px-2.5 py-1 rounded-full" style={{ background: "rgba(244,237,226,0.9)" }}>{discount}% off</div>
          )}
          {isLowStock && (
            <div data-testid="pd-low-stock-badge" className="absolute top-3 right-3 backdrop-blur text-[10px] tracking-editorial-sm uppercase px-2.5 py-1 rounded-full text-white flex items-center gap-1" style={{ background: "rgba(27,25,23,0.85)" }}>
              <Clock size={10} strokeWidth={2} /> Only {product.stock_quantity} left
            </div>
          )}
        </div>
        {images.length > 1 && (
          <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
            {images.map((im, i) => (
              <button
                key={i}
                onClick={() => setImgIdx(i)}
                className={`w-16 h-16 shrink-0 rounded-xl overflow-hidden press ${i === imgIdx ? "" : "opacity-70"}`}
                style={{ border: `1px solid ${i === imgIdx ? "var(--ink)" : "rgba(27,25,23,0.12)"}` }}
              ><img src={resolveImage(im)} alt="" className="w-full h-full object-cover" /></button>
            ))}
          </div>
        )}
      </div>

      <div className="px-5 mt-6">
        {product.brand && <div className="text-[9px] tracking-editorial-sm uppercase" style={{ color: "var(--ink-3)" }}>{product.brand}</div>}
        <h1 data-testid="product-name" className="font-display text-[28px] leading-[1.05] mt-1.5" style={{ color: "var(--ink)" }}>{product.name}</h1>
        <div className="mt-3 flex items-baseline gap-3">
          <span data-testid="product-price" className="font-display-italic text-[28px]" style={{ color: "var(--accent)" }}>₹{Math.round(product.selling_price)}</span>
          {hasMrp && <span className="text-sm line-through" style={{ color: "var(--ink-3)" }}>₹{Math.round(product.mrp)}</span>}
        </div>
        {product.description && <p className="mt-4 text-sm leading-relaxed" style={{ color: "var(--ink-2)" }}>{product.description}</p>}

        <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3">
          {[
            ["Size", product.size],
            ["Weight", product.weight],
            ["Color", product.color],
            ["Shade", product.shade],
            ["Stock", typeof product.stock_quantity === "number" ? String(product.stock_quantity) : null],
          ].filter(([_, v]) => v).map(([k, v]) => (
            <div key={k}>
              <div className="text-[10px] tracking-editorial-sm uppercase" style={{ color: "var(--ink-3)" }}>{k}</div>
              <div className="text-sm mt-0.5">{v}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-4 gap-3">
          <a data-testid="product-call-btn" href={telHref} className="col-span-1 rounded-full h-12 grid place-items-center press" style={{ border: "1px solid rgba(27,25,23,0.15)" }}><Phone size={17} strokeWidth={1.6} /></a>
          <a
            data-testid="product-whatsapp-btn"
            href={waHref}
            target="_blank" rel="noopener noreferrer"
            className={`col-span-3 rounded-full h-12 grid place-items-center press text-[12px] tracking-editorial-sm uppercase text-white`}
            style={{ background: isLowStock ? "var(--ink)" : "#25D366" }}
          >
            {isLowStock ? "Reserve on WhatsApp" : "Enquire on WhatsApp"}
          </a>
        </div>
        <div className="h-12" />
      </div>
      <WhatsAppFAB number={settings?.whatsapp_number} message={`Hello, I am interested in ${product.name}.`} />
    </AppFrame>
  );
}
